import React from 'react'
import { Box, Typography } from '@mui/material'
import { Field, ErrorMessage, FieldArray, FastField } from 'formik'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import AddIcon from '@mui/icons-material/Add'
import policyObjects, { getCategoryKey } from '../resource/policyObjects'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxButton from 'components/custom/AdxButton'

const PolicyEntitlement = ({ formikBag, type }) => {
  const { values, handleChange, handleBlur, setFieldValue, errors, touched, validateForm } =
    formikBag
  const entitlementRules = values.entitlement || []
  const lastEntitlementRule = entitlementRules[entitlementRules.length - 1] || {
    end_year_of_service: 99,
    layer: 0,
  }

  return (
    <Box display="flex" flexDirection="column" rowGap={4}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: ' column', lg: 'row' },
          alignItems: { xs: 'flex-start', lg: 'center' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: 1,
          }}
        >
          <Typography variant="body1" color="dark-text.main" sx={{ mb: 1.5 }}>
            Starting
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              width: 1,
            }}
          >
            <FastField
              as={AdxInput}
              name="eligible_amount"
              // value={values.eligible_amount}
              onChange={handleChange}
              onBlur={(event) => {
                handleBlur(event)
                if (Number(event.target.value) === 0) {
                  setFieldValue('eligible_period', '')
                }
              }}
              type="number"
              errorMessage={' '}
              sx={{ width: { xs: 1, sm: 220 } }}
              isErrorShown={errors.eligible_amount}
            />
            <Field
              as={AdxDropdown}
              name="eligible_period"
              value={values.eligible_period}
              onChange={handleChange}
              sx={{ width: { xs: 1, sm: 220 } }}
              isErrorShown={errors.eligible_period}
            >
              <AdxMenuItem selectedvalue={values.eligible_period} value="day">
                Day
              </AdxMenuItem>
              <AdxMenuItem selectedvalue={values.eligible_period} value="week">
                Week
              </AdxMenuItem>
              <AdxMenuItem selectedvalue={values.eligible_period} value="month">
                Month
              </AdxMenuItem>
            </Field>
            <Typography
              variant="body1"
              color="dark-text.main"
              sx={{ width: { xs: 1, sm: 'auto' } }}
            >
              after hiring date
            </Typography>
          </Box>
          {(errors.eligible_amount || errors.eligible_period) && (
            <AdxErrorMessage>Please fill all the value</AdxErrorMessage>
          )}
        </Box>
      </Box>
      {type === 'WITHOUT' ? null : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1" color="dark-text.main">
            Entitlement rules
          </Typography>
          <Typography variant="body2" color="light-gray-text.main" sx={{ mt: 0.5 }}>
            Set how many leave days staff receive for each years-of-service range.
          </Typography>
          <Box
            width={1}
            border={1}
            borderColor="gray-line.main"
            minWidth={{ xs: 'auto', lg: '600px' }}
            borderRadius="10px"
            marginTop={2}
          >
            <FieldArray
              name="entitlement"
              render={(arrayHelpers) => (
                <>
                  {entitlementRules.map((entitlement, index, fieldArray) => (
                    <Box
                      width={1}
                      key={index}
                      display="flex"
                      flexDirection={{ xs: 'column', lg: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', lg: 'center' }}
                      borderBottom={1}
                      borderColor="gray-line.main"
                      px={3}
                      py={2.5}
                      boxSizing="border-box"
                    >
                      <Box
                        width={1}
                        display="flex"
                        flexDirection={{ xs: 'column', lg: 'row' }}
                        alignItems={{ xs: 'flex-start', lg: 'center' }}
                        marginBottom={{ xs: 6, lg: 0 }}
                        rowGap={{ xs: 2, lg: 0 }}
                        columnGap={{ xs: 0, lg: 4 }}
                      >
                        <Typography minWidth={155} variant="body1" color="light-gray-text.main">
                          {fieldArray.length === 1
                            ? 'All service years'
                            : `Entitlement rule ${index + 1}`}
                        </Typography>
                        <Box
                          display="flex"
                          flexDirection={{ xs: 'column', lg: 'row' }}
                          alignItems="center"
                          columnGap={2}
                          rowGap={1}
                          width={{ xs: 1, lg: 390 }}
                        >
                          <Field
                            name={`entitlement.${index}.start_year_of_service`}
                            as={AdxInput}
                            onChange={handleChange}
                            type="number"
                            readOnly
                            sx={{
                              width: { xs: 1, lg: 78 },
                              backgroundColor: 'gray-background.main',
                              borderRadius: 2,
                            }}
                            sxInput={{ outline: 'none' }}
                          />
                          <Typography variant="body1" color="dark-text">
                            to
                          </Typography>
                          <Field
                            name={`entitlement.${index}.end_year_of_service`}
                            as={AdxInput}
                            type="number"
                            readOnly={index < values.entitlement.length - 1}
                            onChange={handleChange}
                            onBlur={(e) => {
                              if (Number(e.target.value) < Number(e.target.min)) {
                                setFieldValue(
                                  `entitlement.${index}.end_year_of_service`,
                                  e.target.min
                                )
                              }
                              if (Number(e.target.value) > Number(e.target.max)) {
                                setFieldValue(
                                  `entitlement.${index}.end_year_of_service`,
                                  e.target.max
                                )
                              }
                            }}
                            inputProps={{
                              min: entitlementRules[index].start_year_of_service + 1,
                              max: 99,
                            }}
                            sx={{
                              width: { xs: 1, lg: 78 },
                              backgroundColor:
                                index < entitlementRules.length - 1 && 'gray-background.main',
                              borderRadius: index < entitlementRules.length - 1 && 2,
                            }}
                            sxInput={{
                              outline: index < entitlementRules.length - 1 && 'none',
                            }}
                          />
                          <Typography variant="body1" color="dark-text">
                            years of service
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          flexDirection={{ xs: 'column', lg: 'row' }}
                          alignItems="center"
                          columnGap={2}
                          rowGap={1}
                          width={{ xs: 1, lg: 300 }}
                        >
                          <Field
                            name={`entitlement.${index}.amount`}
                            as={AdxInput}
                            onChange={handleChange}
                            type="number"
                            sx={{ width: { xs: 1, lg: 80 } }}
                            isErrorShown={
                              errors?.entitlement?.length &&
                              touched?.entitlement?.length &&
                              errors?.entitlement[index]?.amount &&
                              touched?.entitlement[index]?.amount
                            }
                          />
                          <Typography variant="body1" color="dark-text">
                            days per cycle
                          </Typography>
                          <ErrorMessage
                            name={`entitlement.${index}.amount`}
                            component={AdxErrorMessage}
                          />
                        </Box>
                      </Box>
                      <Box
                        width={{ xs: 1, lg: 'auto' }}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        justifySelf={{ xs: 'flex-end', lg: null }}
                        onClick={() => {
                          arrayHelpers.remove(index)
                          setFieldValue(`entitlement.${index - 1}.end_year_of_service`, 99)
                        }}
                      >
                        {index === fieldArray.length - 1 && fieldArray.length !== 1 ? (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            aria-label="Delete entitlement rule"
                            sx={{ cursor: 'pointer' }}
                          >
                            <RemoveCircleIcon
                              sx={{
                                fontSize: 20,
                              }}
                              color="error"
                            />
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  ))}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <AdxButton
                      variant={'ghost'}
                      disabled={lastEntitlementRule.end_year_of_service === 99}
                      onClick={() => {
                        arrayHelpers.push({
                          layer: lastEntitlementRule.layer + 1,
                          amount: 0,
                          start_year_of_service: lastEntitlementRule.end_year_of_service,
                          end_year_of_service: 99,
                        })
                      }}
                      sx={{ paddingX: 3, paddingY: 4 }}
                    >
                      <AddIcon sx={{ fontSize: 20, marginRight: 1 }} />
                      <Typography
                        variant="body1"
                        color={
                          lastEntitlementRule.end_year_of_service === 99
                            ? 'gray-inactive-button.main'
                            : 'alternate.main'
                        }
                      >
                        Add entitlement rule
                      </Typography>
                    </AdxButton>
                  </Box>
                </>
              )}
            />
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
        <Typography variant="body1" color="dark-text.main">
          This entitlement is available to:
        </Typography>
        <Box display="flex" flexDirection="column" rowGap={2.5}>
          {Object.entries(policyObjects).map(([k, value]) => (
            <Box
              key={k}
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              columnGap={2}
              rowGap={0.5}
            >
              <Typography width={150}>{k}</Typography>
              <FieldArray
                name={`category.${getCategoryKey(k)}`}
                render={(arrayHelpers) => (
                  <>
                    {value.map((value, index, arr) => (
                      <React.Fragment key={index}>
                        <AdxFormControlLabel
                          type="no-fill"
                          label={value.name}
                          control={
                            <Field
                              as={AdxCheckbox}
                              name={`category.${getCategoryKey(k)}.${values.category[
                                getCategoryKey(k)
                              ].findIndex((el) => el.data === value.data)}.status`}
                              checked={
                                values.category[getCategoryKey(k)][
                                  values.category[getCategoryKey(k)].findIndex(
                                    (el) => el.data === value.data
                                  )
                                ].status === 1
                                  ? true
                                  : false
                              }
                              onChange={(e) => {
                                setFieldValue(
                                  `category.${getCategoryKey(k)}.${values.category[
                                    getCategoryKey(k)
                                  ].findIndex((el) => el.data === value.data)}.status`,
                                  e.target.checked ? 1 : 2
                                )
                                setTimeout(() => {
                                  validateForm()
                                })
                              }}
                            />
                          }
                          sx={{ width: 120 }}
                        />
                      </React.Fragment>
                    ))}
                  </>
                )}
              />
              <ErrorMessage name={`category.${getCategoryKey(k)}`} component={AdxErrorMessage} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default PolicyEntitlement
